import { Injectable } from '@angular/core'
import { merge, Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap, toArray } from 'rxjs/operators'
import { EntityConfigService } from '../../dto/services/entity-config.service'
import { EntityService } from '../../dto/services/entity.service'
import { SelectionService } from '../../dto/services/selection.service'
import { StoredEntitiesWrapper } from './stored-entities.wrapper'
import { select, Store } from '@ngrx/store'
import { DtoActions, DtoSelectors } from '../../../root-store/dto-store/dto.index'
import { cloneDeep, isEqual } from 'lodash'
import DtoStoreUtils from '../../../root-store/dto-store/dto-store.util'
import { RootStoreState } from '../../../root-store/root-index'
import { DtoWrapper, LoadingState } from '../../../root-store/dto-store/dto.state'
import { AdditionalQualifier } from '../../dto/models/table-state.model'
import { Entity, EntityData, Field } from '../../dto'
import { CatalogueActions } from '../../../root-store/catalogue-store/catalogue-index'

@Injectable({
  providedIn: 'root'
})
export class StoredEntityService {
  constructor(
    protected entityService: EntityService,
    // protected entityFacade: EntityFacadeService,
    protected entityConfigService: EntityConfigService,
    protected selectionService: SelectionService,
    private store$: Store<RootStoreState.State>
  ) {}

  getEntity$(
    type: string,
    id: number,
    options?: { enforceFreshBackendRequest?: boolean; partialEntityIsSufficient?: boolean }
  ): Observable<Entity> {
    // console.log('[getEntity$]');
    const hasNoConfigResult$: Observable<Entity> = this.entityConfigService.hasConfigurations$.pipe(
      filter((hasConfigs) => !hasConfigs),
      map(() => null)
    )

    const hasConfigResult$: Observable<Entity> = this.entityConfigService.hasConfigurations$.pipe(
      filter((hasConfigs) => hasConfigs),
      switchMap(() =>
        this.hasEntityOrIsEntityPending$(type, id, { partialEntityIsSufficient: options?.partialEntityIsSufficient })
      ),
      take(1),
      switchMap((hasEntityOrPending) => {
        // console.log('[getEntity$] hasEntityOrPending, enforceFreshBackendRequest', hasEntityOrPending, enforceFreshBackendRequest);
        // if (hasEntityOrPending && !enforceFreshBackendRequest) {
        if (hasEntityOrPending && !options?.enforceFreshBackendRequest) {
          // console.log('getting entity from store', type, id);
          return this.getEntityFromStore$(type, id)
        }

        // console.log('getting entity from be', type, id);
        return this.setPendingStateForEntity$(type, id).pipe(
          switchMap(() =>
            this.entityConfigService.getConfig$(type).pipe(
              take(1),
              filter((config) => !!config),
              switchMap((config) =>
                this.entityService.getEntity(config.fields, type, id).pipe(
                  // with that the pending state gets canceled.
                  tap((e) => this.addOrUpdateEntity(e)),
                  switchMap((e) => this.addPartialEntitiesFromEntity$(config.fields, e).pipe(map((_) => e))),
                  switchMap((e) => this.getEntityFromStore$(type, id)),
                  catchError((err) => {
                    console.error('catched error while getting entity from be.', err)
                    return this.cancelPendingStateForEntity$(type, id)
                  })
                )
              )
            )
          )
        )
      }),
      filter((entity) => !!entity?.fields)
    )

    return merge(hasConfigResult$, hasNoConfigResult$)
  }

  getEntities$(type: string, ids: number[]): Observable<Entity[]> {
    return this.store$.pipe(
      select(DtoSelectors.selectDTOsWithTypeAndIds, { type, ids }),
      map((dtoWrappers) =>
        dtoWrappers.map((dtoWrapper) => (!!dtoWrapper?.dto ? cloneDeep(dtoWrapper.dto) : dtoWrapper?.dto))
      ),
      distinctUntilChanged((a, b) => isEqual(a, b))
    )
  }

  addAndGetPartialEntity$(refType: string, partialEntity: object): Observable<Entity> {
    const entity = new Entity()
    entity.type = refType
    entity.fields = partialEntity
    const id = +partialEntity?.['id']
    // check if it already exists and the _repr_ has changed. if yes -> get complete entity fresh from backend.
    return this.hasEntity$(refType, id).pipe(
      take(1),
      switchMap((hasEntity) => {
        if (!hasEntity) {
          this.addOrUpdateEntity(entity, { isPartial: true })
          return this.getEntity$(refType, id, { partialEntityIsSufficient: true })
        }
        return this.getDtoWrapperFromStore$(refType, id).pipe(
          switchMap((dw) => {
            // if the _repr_ field of a fully loaded entity has changed, we need to load it fully from the backend again.
            const enforceFreshBackendRequest =
              dw.loadingState === LoadingState.Full && dw.dto?.fields['_repr_'] !== partialEntity['_repr_']
            // partials state is sufficient when no update from backend is needed
            const partialEntityIsSufficient = !enforceFreshBackendRequest

            return this.getEntity$(refType, id, { enforceFreshBackendRequest, partialEntityIsSufficient })
          })
        )
      })
    )
  }

  /** calls filter on entity-service, stores the entities in the store and returns an entityData object with an empty data array. */
  // should that return an entity-data obj and a hot entity-obs?
  filter$(
    fields: Field[],
    type: string,
    page: number,
    pageSize: number,
    mainId: number,
    qualifier: string,
    sorting: string,
    additionalQualifier?: AdditionalQualifier,
    ownerType?: string,
    ownerId?: number
  ): Observable<EntityData> {
    return this.entityService
      .filter(fields, type, page, pageSize, mainId, qualifier, sorting, additionalQualifier, ownerType, ownerId)
      .pipe(
        tap((entityData) => this.addOrUpdateEntityData(entityData)),
        // add partial entities into the store.
        switchMap((ed) => this.addPartialEntitiesFromEntityData$(fields, ed).pipe(map((_) => ed)))
      )
  }

  // filterForEntityDataAndEntities$(
  //   fields: Field[], type: String, page: Number, pageSize: Number, mainId: number, qualifier: String, sorting: String, additionalQualifier?: AdditionalQualifier): Observable<[EntityData, Entity[]]> {
  //
  //     return this.filter$(fields, type, page, pageSize, mainId, qualifier, sorting, additionalQualifier).pipe(
  //       take(1),
  //       switchMap(entityData => {
  //         return this.getEntities$(<string>type, entityData.data.map(e => e['id'])).pipe(
  //           // does that casting work?
  //           map(entities => [entityData, entities] as [EntityData, Entity[]])
  //         );
  //       })
  //     );
  // }

  /** Creates a StoredEntitiesWrapper, which will manage a list of entities associated with a entity-data instance.*/
  createStoredEntitiesWrapper(): StoredEntitiesWrapper {
    const sew = new StoredEntitiesWrapper(this)
    return sew
  }
  // getEntityDataFromUrl?

  createEntity$(fields: Field[], type: String, data): Observable<Entity> {
    // (Hopefully) temporary solution to evict outdated catalogues from the catalogue cache
    if (type === 'CatalogueEntry') {
      this.store$.dispatch(CatalogueActions.catalogueModified({ id: data['mainId'] }))
    }
    return this.entityService.createEntity(fields, type, data).pipe(
      tap((e) => this.addOrUpdateEntity(e)),
      switchMap((e) => this.getEntity$(e.type, e.fields['id']))
    )
  }

  updateEntity$(fields: Field[], type: string, id: number, data, _lastUpdate_?: Date): Observable<Entity> {
    const selectionField = fields.find((f) => f.type === 'Selection' && Object.keys(data).includes(f.field))
    if (!!selectionField) {
      const isSelected = !!data[selectionField.field]

      return isSelected ? this.selectionService.select(type, [id]) : this.selectionService.unselect(type, [id])
    }

    return this.entityService.updateEntity(fields, type, id, data, _lastUpdate_).pipe(
      tap((e) => {
        // console.log('updated entity. ', e, data)
        this.addOrUpdateEntity(e)
        this.checkAndRefreshInternalCaches(type, e)
      }),
      switchMap((e) => this.getEntity$(<string>type, id))
    )
  }

  // new Method to update an entity. Not necessary to convert date fields. Attention: To use this, dates have to be converted before.
  updateEntityV2$(type: string, id: number, data, _lastUpdate_: string): Observable<Entity> {
    return this.entityService.updateEntityV2(type, id, data, _lastUpdate_).pipe(
      tap((e) => this.addOrUpdateEntity(e)), // update Entity in Store
      tap((e) => this.checkAndRefreshInternalCaches(type, e)),
      switchMap((e) => this.getEntity$(<string>type, id))
    )
  }

  private checkAndRefreshInternalCaches(type: string, e: Entity) {
    if (type === 'CatalogueEntry') {
      this.store$.dispatch(CatalogueActions.catalogueModified({ id: e.fields['mainId'] }))
    } else if (type === 'Catalogue') {
      this.store$.dispatch(CatalogueActions.catalogueModified({ id: e.fields['id'] }))
    }
  }

  deleteEntity$(type: string, id: number): Observable<any> {
    let catalogueEviction: Observable<any> = of(undefined)
    if (type === 'CatalogueEntry') {
      catalogueEviction = this.getEntity$(type, id).pipe(
        tap((entity) => this.store$.dispatch(CatalogueActions.catalogueModified({ id: entity.fields['mainId'] })))
      )
    } else if (type === 'Catalogue') {
      this.store$.dispatch(CatalogueActions.catalogueModified({ id }))
    }
    return catalogueEviction.pipe(
      switchMap(() =>
        this.entityService.deleteEntity(type, id).pipe(
          take(1),
          tap((_) => this.removeEntity(type, id))
        )
      )
    )
  }

  clearStore(): void {
    this.store$.dispatch(DtoActions.resetAllDTOs())
  }

  // deleteEntity(type: string, id: number): void {
  //   this.deleteEntity$(type, id).pipe(
  //     take(1),
  //     catchError(err => {
  //       console.log('error while deleting entity.', err);
  //       return null;
  //     })
  //   ).subscribe();
  // }

  // private store methods (were previously in extra service)

  private addOrUpdateEntity(entity: Entity, options?: { isPartial?: boolean }) {
    if (!entity?.fields || !entity?.type) {
      // throw Error('can not add null-entity to store.');
      console.error('can not add null entity to store.', entity)
      return
    }
    console.log('upsering ', entity)
    this.store$.dispatch(
      DtoActions.upsertDTO({ dto: DtoStoreUtils.packEntity(entity, { isPartial: options?.isPartial }) })
    )
  }

  private setPendingStateForEntity(type: string, id: number): void {
    this.store$.dispatch(DtoActions.setPendingState({ storeId: DtoStoreUtils.getStoreIdFromTypeAndId(type, id) }))
  }

  /** this version of setPending does not delete existing entity-state.
   * (useful to not create short periods of no entity-data, when enforcing an update from the backend.) */
  private setPendingStateForEntity$(type: string, id: number): Observable<any> {
    return this.hasEntity$(type, id).pipe(
      switchMap((hasEntity) => {
        if (hasEntity) {
          return this.getEntity$(type, id).pipe(
            tap((entity) => {
              // manual setting pending state on existing item.
              const dtoWrapper = DtoStoreUtils.packEntity(entity)
              dtoWrapper.pending = true
              this.store$.dispatch(DtoActions.setPendingStateForExistingEntity({ dto: dtoWrapper }))
            })
          )
        }
        this.setPendingStateForEntity(type, id)
        return of(null)
      })
    )
  }

  private cancelPendingStateForEntity(type: string, id: number): void {
    this.store$.dispatch(
      DtoActions.setPendingState({ storeId: DtoStoreUtils.getStoreIdFromTypeAndId(type, id), cancelPending: true })
    )
  }

  private cancelPendingStateForEntity$(type: string, id: number): Observable<any> {
    return this.hasEntity$(type, id).pipe(
      switchMap((hasEntity) => {
        if (hasEntity) {
          return this.getEntity$(type, id).pipe(
            tap((entity) => {
              // this removes the pending state
              // this.addOrUpdateEntity(entity);
              const dtoWrapper = DtoStoreUtils.packEntity(entity)
              this.store$.dispatch(DtoActions.setPendingStateForExistingEntity({ dto: dtoWrapper }))
            })
          )
        }
        this.cancelPendingStateForEntity(type, id)
        return of(null)
      })
    )
  }

  private addOrUpdateEntities(entities: Entity[]) {
    // console.log('addOrUpdateEntities', entities);
    this.store$.dispatch(DtoActions.upsertDTOs({ dtos: entities.map((entity) => DtoStoreUtils.packEntity(entity)) }))
  }

  private addOrUpdateEntityData = (entityData: EntityData) =>
    this.addOrUpdateEntities(
      entityData.data.map((fields) => DtoStoreUtils.convertFieldsToEntity(fields, entityData.type))
    )

  // private getEntityFromStore$(type: string, id: number): Observable<Entity> {
  //   return this.store$.pipe(
  //     select(DtoSelectors.selectDtoWithTypeAndId, {type, id}),
  //     map(dtoWrapper => dtoWrapper?.dto),
  //     map(dto => !!dto ? cloneDeep(dto) : dto),
  //     distinctUntilChanged((a, b) => isEqual(a, b)),
  //     );
  // }
  private getEntityFromStore$(type: string, id: number): Observable<Entity> {
    return this.getDtoWrapperFromStore$(type, id).pipe(
      map((dtoWrapper) => dtoWrapper?.dto),
      distinctUntilChanged((a, b) => a?.fields['_lastUpdate_'] === b?.fields['_lastUpdate_'])
    )
  }

  private getDtoWrapperFromStore$(type: string, id: number): Observable<DtoWrapper> {
    return this.store$.pipe(
      select(DtoSelectors.selectDtoWithTypeAndId, { type, id }),
      map((dto) => (!!dto ? cloneDeep(dto) : dto)),
      distinctUntilChanged((a, b) => isEqual(a, b))
    )
  }

  private hasEntity$(type: string, id: number, options?: { partialEntityIsSufficient?: boolean }): Observable<boolean> {
    // console.log('[hasEntity$]', type, id);
    if (!id) {
      return of(false)
    }
    return this.store$.pipe(
      select(DtoSelectors.selectDtoExistWithTypeAndId, {
        type,
        id,
        partialEntityIsSufficient: options?.partialEntityIsSufficient
      })
      // tap(has => console.log('[hasEntity$]:', has))
    )
  }

  private hasEntityOrIsEntityPending$(
    type: string,
    id: number,
    options?: { partialEntityIsSufficient?: boolean }
  ): Observable<boolean> {
    // console.log('[hasEntityOrIsEntityPending$]', type, id);
    return this.store$.pipe(
      select(DtoSelectors.selectDtoExistOrIsPendingWithTypeAndId, {
        type,
        id,
        partialEntityIsSufficient: options?.partialEntityIsSufficient
      }),
      // tap(has => console.log('[hasEntityOrIsEntityPending$]:', has))
      // distinctUntilChanged()

      distinctUntilChanged((a, b) => {
        const result = isEqual(a, b)
        // console.log(`hasEntityOrIsEntityPending$-distinctUntilChanged: ${a} == ${b} => ${result}`);
        return result
      })
    )
  }

  // private isEntityPending$(type: string, id: number): Observable<boolean> {
  //   return this.store$.pipe(
  //     select(DtoSelectors.selectDtoIsPendingWithTypeAndId, {type, id})
  //     );
  // }

  private removeEntity(type: string, id: number): void {
    this.store$.dispatch(DtoActions.removeDTO({ id: DtoStoreUtils.getStoreIdFromTypeAndId(type, id) }))
  }

  private resetEntities$(): void {
    this.store$.dispatch(DtoActions.resetAllDTOs())
  }

  private addPartialEntitiesFromEntityData$(fields: Field[], entityData: EntityData): Observable<Entity[]> {
    const refFields = fields.filter((f) => Field.isEntityReference(f) && f.field !== 'mainId')
    const entities = entityData.data

    const partialEntitiesWithRefType = refFields.map((refField) =>
      entities
        .filter((e) => !!e[refField.field])
        .map((entity) => ({ entity: entity[refField.field], refType: refField.referenceType }))
    )
    const partialEntitiesWithRefTypeFlattened = [].concat(...partialEntitiesWithRefType) as {
      entity: any
      refType: string
    }[]

    const length = partialEntitiesWithRefTypeFlattened.length
    if (length === 0) {
      return of([])
    }

    const result$ = of(partialEntitiesWithRefTypeFlattened).pipe(
      mergeMap((arr) => arr),
      mergeMap((pe) => this.addAndGetPartialEntity$(pe.refType, pe.entity)),
      take(length),
      toArray()
    )

    return result$
  }

  private addPartialEntitiesFromEntity$(fields: Field[], entity: Entity): Observable<Entity[]> {
    const refFields = fields.filter((f) => Field.isEntityReference(f) && f.field !== 'mainId')
    const partialEntitiesWithRefType = refFields
      .map((rf) => ({ entity: entity.fields[rf.field], refType: rf.referenceType }))
      .filter((pe) => !!pe.entity)

    const length = partialEntitiesWithRefType.length
    if (length === 0) {
      return of([])
    }

    const result$ = of(partialEntitiesWithRefType).pipe(
      mergeMap((arr) => arr),
      mergeMap((pe) => this.addAndGetPartialEntity$(pe.refType, pe.entity)),
      take(length),
      toArray()
    )

    return result$
  }
}
